import { Box, Button, makeStyles } from '@material-ui/core';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { useDatadogClickTracking } from '../../datadog';

const MAX_INITIAL_SLOTS = 6;

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    slotGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
        [theme.breakpoints.up('sm')]: {
            gridTemplateColumns: '1fr 1fr 1fr',
        },
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    slotButton: {
        height: 48,
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 700,
        textTransform: 'none',
        transition: 'all 0.15s ease',
    },
    slotButtonSelected: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    },
    showMoreWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 6,
        minHeight: 22,
    },
    showMoreButton: {
        fontSize: 9,
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        color: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}14`,
        border: `1px solid ${theme.palette.primary.main}33`,
        borderRadius: 12,
        padding: '4px 12px',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: `${theme.palette.primary.main}26`,
        },
    },
    noTimesMessage: {
        textAlign: 'center',
        padding: '32px 0',
        color: theme.palette.text.disabled,
        fontSize: 14,
        fontStyle: 'italic',
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 12,
    },
    bookButton: {
        width: '100%',
        height: 48,
        borderRadius: 12,
        fontWeight: 700,
        fontSize: 14,
        textTransform: 'none',
        marginTop: 12,
        transition: 'all 0.15s ease',
    },
    bookButtonDisabled: {
        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
        },
    },
}));

function formatTimeString(time, timezone) {
    return new Date(time)
        .toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: timezone,
        })
        .toLowerCase()
        .replace(' ', '');
}

export default function ClinicianTimeSelectionContainer({
    clinician,
    availability,
    selectedDate,
    activeSelection,
    onSlotSelect,
    onBookVisit,
    timezone,
}) {
    const classes = useStyles();
    const [showAllSlots, setShowAllSlots] = useState(false);
    const bookButtonRef = useRef(null);

    const { trackClick } = useDatadogClickTracking('time-slot-select', {
        page: 'book-with-clinician',
        feature: 'clinician-time-select',
    });

    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    const slots = availability[dateKey] || [];
    const isThisCardSelected = activeSelection?.clinicianId === clinician.uuid;
    const selectedTime = isThisCardSelected ? activeSelection?.time : null;

    const displayedSlots = useMemo(() => {
        if (showAllSlots) return slots;
        if (selectedTime && slots.length > MAX_INITIAL_SLOTS) {
            const selectedIdx = slots.indexOf(selectedTime);
            if (selectedIdx >= MAX_INITIAL_SLOTS) {
                const visible = slots.slice(0, MAX_INITIAL_SLOTS);
                visible[MAX_INITIAL_SLOTS - 1] = selectedTime;
                return visible;
            }
        }
        return slots.slice(0, MAX_INITIAL_SLOTS);
    }, [showAllSlots, slots, selectedTime]);
    const hasMoreSlots = slots.length > MAX_INITIAL_SLOTS;

    const handleSlotClick = time => {
        trackClick();
        onSlotSelect(time, clinician);
        // Scroll book button into view
        // Wait for any layout changes (show more expansion) to settle
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                bookButtonRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            });
        });
    };

    const [isBooking, setIsBooking] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        []
    );

    const handleBookClick = async () => {
        if (selectedTime && onBookVisit) {
            setIsBooking(true);
            try {
                await onBookVisit();
            } finally {
                if (isMountedRef.current) setIsBooking(false);
            }
        }
    };

    return (
        <Box className={classes.root}>
            {slots.length > 0 ? (
                <>
                    <Box className={classes.slotGrid}>
                        {displayedSlots.map(time => {
                            const isSelected = selectedTime === time;
                            return (
                                <Button
                                    key={time}
                                    color='primary'
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    className={`${classes.slotButton} ${
                                        isSelected ? classes.slotButtonSelected : ''
                                    }`}
                                    onClick={() => handleSlotClick(time)}
                                >
                                    {formatTimeString(time, timezone)}
                                </Button>
                            );
                        })}
                    </Box>
                    <Box className={classes.showMoreWrapper}>
                        {hasMoreSlots ? (
                            <button
                                className={classes.showMoreButton}
                                onClick={() => setShowAllSlots(!showAllSlots)}
                            >
                                {showAllSlots
                                    ? 'Show Less'
                                    : `+${slots.length - MAX_INITIAL_SLOTS} More Times`}
                            </button>
                        ) : (
                            <span style={{ height: 22 }} />
                        )}
                    </Box>
                </>
            ) : (
                <Box className={classes.noTimesMessage}>No times available for this date</Box>
            )}

            <Button
                ref={bookButtonRef}
                disabled={!selectedTime || isBooking}
                onClick={handleBookClick}
                variant='contained'
                color='primary'
                className={`${classes.bookButton} ${
                    !selectedTime || isBooking ? classes.bookButtonDisabled : ''
                }`}
            >
                {isBooking ? 'Booking...' : 'Book Visit'}
            </Button>
        </Box>
    );
}
